import { useState } from 'react';
import { useTranslation } from 'next-i18next';
import NiceModal from '@ebay/nice-modal-react';
import { useSwrWorkplaceToggleFollower } from '@hooks/useSwrApi/endpoints/workplace';
import { useSwrJobApply } from '@hooks/useSwrApi/endpoints/job';
import { useAuthClient } from '@hooks/common/useAuthClient/useAuthClient';
import { PersonalDataModal } from '@components/templates/job/apply/PersonalDataModal';
import { MissingLocationModal } from '@components/templates/job/apply/MissingLocationModal';
import { JOB_APPLY_WEB_TYPE } from 'src/constants';
import { useExtendedRouter } from '@hooks/next-routing-wrappers';
import { ClaimWorkplaceModal } from '@components/templates/employer/profile/details/ClaimWorkplaceModal';

// Interface
// ************************************
interface EmployerProfileActionsProps {
  slug: string;
  name: string;
  locale: Locale;
  instaJobSlug?: string;
  profileCategories?: ApiSchema<'ProfileCategory'>[];
  hasAppliedInitial?: boolean;
  isFollowerInitial?: boolean;
}

/**
 * @description Employer Profile Talent Actions hook.
 * Manages actions on a workplace profile page, such as:
 * - follow / unfollow an employer
 * - apply to employer
 * - claim workplace
 *
 * Props:
 * - **slug** - string. The workplace's unique slug.
 * - **name** - string. The workplace name.
 * - **locale** - Locale. The current app locale.
 * - **instaJobSlug** - string. The slug for applying directly to the employer.
 * - **profileCategories** - EmployerProfileCategory[]. The list of profile categories for the employer.
 * - **hasAppliedInitial** - boolean. Initial state indicating if the talent has applied to the employer.
 * - **isFollowerInitial** - boolean. Initial state indicating if the user is following the employer.
 *
 * Returns:
 * - **hasApplied** - Indicates whether the talent has already applied to the employer.
 * - **isApplying** - A flag indicating whether the job apply action is in progress.
 * - **handleApply** -  Function to direct apply to the employer.
 * - **isFollower** - Indicates whether the talent is currently following the employer.
 * - **isTogglingFollower** - A flag indicating whether the follow/unfollow action is in progress.
 * - **handleToggleFollower** - Function to toggle the follow status of the employer.
 */
export const useEmployerProfileActions = (props: EmployerProfileActionsProps) => {
  // Destructure props
  const {
    slug,
    name,
    locale,
    instaJobSlug,
    hasAppliedInitial,
    isFollowerInitial,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Router
  const { withLocale } = useExtendedRouter();

  // State
  // ******************************************************
  // Tracks whether the talent is currently following the employer
  const [isFollower, setIsFollower] = useState(isFollowerInitial);

  // Tracks whether the talent has applied to the employer
  const [hasApplied, setHasApplied] = useState(hasAppliedInitial);

  // Authentication client side
  // ******************************************************
  const { isAuth, redirectToLogin } = useAuthClient();

  // SWR: Add / remove employer follower.
  // ******************************************************
  const {
    trigger: triggerToggleFollower,
    isMutating: isTogglingFollower,
  } = useSwrWorkplaceToggleFollower(
    slug,
    isFollower ? 'remove' : 'add',
    locale,
    () => {
      setIsFollower((prev) => !prev);
    },
    isFollower
      ? t('flash.message.follow.employer.subscribe')
      : t('flash.message.follow.employer.unsubscribe'),
  );

  // SWR: Direct apply to employer
  // ******************************************************
  const {
    trigger: triggerApply,
    isMutating: isApplying,
  } = useSwrJobApply({
    slug: instaJobSlug ?? '',
    locale,
    successCallback: () => {
      setHasApplied(true);
    },
    errorCallback: (errorObj) => {
      if (!errorObj || !errorObj.errors?.length) {
        return;
      }

      const errorCode = errorObj.errors[0].code;

      if (errorCode === 452 || errorCode === 453) {
        void NiceModal.show(MissingLocationModal, {
          locale,
          onNotNeeded: () => { void NiceModal.show(PersonalDataModal, { locale }); },
        });
      }
    },
    toastsOptions: {
      successToastText: t('employer.application_successful'),
    },
  });

  // Handler: Toggles the follow status of the employer
  // ******************************************************
  const handleToggleFollower = () => {
    if (isAuth) {
      void triggerToggleFollower();
    } else {
      redirectToLogin(encodeURIComponent(withLocale(`/company-profile/${slug}`)));
    }
  };

  // Handler: Apply to employer
  // ******************************************************
  const handleApply = (profileCategoryId: number) => {
    void triggerApply({
      accountProfileCategoryId: profileCategoryId,
      appType: JOB_APPLY_WEB_TYPE,
    });
  };

  // Handler: Claim profile
  // ******************************************************
  const handleClaimProfile = () => {
    if (isAuth) {
      void NiceModal.show(ClaimWorkplaceModal, {
        workplaceName: name,
        workplaceSlug: slug,
      });
    } else {
      redirectToLogin(encodeURIComponent(withLocale(`/company-profile/${slug}`)));
    }
  };

  // Return
  // ***********************************************
  return {
    hasApplied,
    isApplying,
    handleApply,
    isFollower,
    isTogglingFollower,
    handleToggleFollower,
    handleClaimProfile,
  };
};
